/**
 * 外車首次綁定（openspec add-external-driver-grab）
 * 路徑 /ext/bind?back=/ext/batch/{token}。用 LINE_USER token 打綁定端點，成功後拿到 EXTERNAL_DRIVER token 回到批次頁。
 */
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import { useLiff } from '@/contexts/LiffContext'
import ExtDriverForm from './ExtDriverForm'
import { ExtShell, EXT_MAIN } from './ExtGuard'
import { bindExtDriver } from './extService'
import { ERR_PLATFORM_DRIVER, ExtApiError, ExtNotBoundError, extErrorMessage, extLogin } from './extApiClient'
import type { ExtDriver } from './types'

const ExtBindPage: React.FC = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { lineUser, login } = useLiff()
  const back = params.get('back') || '/ext/bind'
  const [ready, setReady] = useState(false)
  const [fatal, setFatal] = useState<string | null>(null)
  const [initial, setInitial] = useState<ExtDriver | null>(null)

  // 每次都登入一次：已綁定者直接回去；未綁定者拿 LINE_USER token，
  // 若該 LINE id 曾綁過（被停用／車號被接手）就把既有資料帶出來預填（使用者 2026-09-19）
  useEffect(() => {
    let cancelled = false
    const prepare = async () => {
      try {
        await extLogin()
        if (!cancelled) navigate(back, { replace: true })
      } catch (err) {
        if (cancelled) return
        if (err instanceof ExtNotBoundError) { setInitial(err.existing); setReady(true); return }
        if (err instanceof ExtApiError && err.code === ERR_PLATFORM_DRIVER) { setFatal(err.message); return }
        setFatal(extErrorMessage(err))
      }
    }
    prepare()
    return () => { cancelled = true }
  }, [back, navigate])

  const handleSubmit = async (req: Parameters<typeof bindExtDriver>[0]) => {
    try {
      const r = await bindExtDriver(req)
      if (!r.bound) throw new Error('綁定未完成，請再試一次')
      navigate(back, { replace: true })
    } catch (err) {
      if (err instanceof ExtApiError && err.code === 'LNE-0105') {
        // LINE_USER token 失效：重新授權 LIFF 再來
        await login(true)
        return
      }
      throw new Error(extErrorMessage(err))
    }
  }

  return (
    <ExtShell>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LocalShippingIcon sx={{ color: EXT_MAIN }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: EXT_MAIN }}>同業司機綁定</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lineUser?.displayName ? `${lineUser.displayName}，` : ''}第一次接單請先填車號與姓名，之後點連結就直接看到放櫃狀態。
        </Typography>
        {fatal ? (
          <Alert severity="warning">{fatal}</Alert>
        ) : !ready ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: EXT_MAIN }} /></Box>
        ) : (
          <>
            {initial && <Alert severity="info" sx={{ mb: 2 }}>已帶入您先前綁定的資料，確認或修改後送出即可重新啟用。</Alert>}
            <ExtDriverForm key={initial?.id ?? 'new'} initial={initial} submitLabel={initial ? '重新綁定' : '完成綁定'} onSubmit={handleSubmit} />
          </>
        )}
      </Paper>
    </ExtShell>
  )
}

export default ExtBindPage
